import { SoftDeleteFlag, Timestamps, Versioned } from '@bofrak-backend/shared';

export interface Principal {
  id: string;
  created_at: string;
  deleted_at: string;
  updated_history: string[];
  merchant_id: string;
  role_id: string;
}

export interface PrincipalList {
  principals: Principal[];
  meta: Meta;
}

export interface Policy {
  id: string;
  policy: string;
  action_id: string;
  resource_id: string;
  description: string;
  created_at: string;
  deleted_at: string;
  updated_history: string[];
}

export interface Action {
  id: string;
  description: string;
  verb: string;
  resource_id: string;
  created_at: string;
  updated_at: string;
  deleted_at: string;
  update_history: string[];
}

export interface Resource {
  id: string;
  description: string;
  path: string;
  created_at: string;
  updated_at: string;
  deleted_at: string;
  update_history: string;
}

export interface Meta {
  num_read: number;
  next_cursor: string;
}

export interface ActionList {
  actions: Action[];
  meta: Meta;
}

export interface ResourceList {
  resources: Resource[];
  meta: Meta;
}

export interface PolicyList {
  policies: Policy[];
  meta: Meta;
}

export interface Role {
  id: string;
  description: string;
  policies: string[];
  created_at: string;
  resources: string[];
  deleted_at: string;
  updated_history: string[];
  role_id: string;
}

export interface RoleList {
  roles: Role[];
  meta: Meta;
}

export interface MultiplePolicyList {
  policies: Policy[];
  meta: {
    items_created: number;
  };
}

export interface MultiplePolicyBody {
  merchant_id: string;
  policies: PolicyBody[];
}

export interface PolicyBody {
  action_id: string;
  resource_id: string;
  description: string;
}

// V2 types

export enum ResourceTypeV2 {
  PATH = 'PATH',
  APP = 'APP',
  /** A business action (`orders:transfer.dispatch`), checked server-side. */
  ACTION = 'ACTION',
}

export enum ActionV2 {
  GET = 'GET',
  POST = 'POST',
  PUT = 'PUT',
  DELETE = 'DELETE',
  OPEN = 'OPEN',
  /** The only verb of an `ACTION` resource. */
  PERFORM = 'PERFORM',
}

export interface ResourceV2 {
  name: string;
  type: ResourceTypeV2;
}

export interface PermissionV2 extends Timestamps, Versioned {
  id: string;
  resource: ResourceV2;
  action: ActionV2;
  description: string;
}

export interface CreatePermissionV2 {
  resource: ResourceV2;
  action: ActionV2;
  description: string;
}

export interface GetPermissionV2 {
  id: string;
}

export interface GetAllPermissionsV2 {
  cursor?: string;
  count?: number;
  include_deleted?: SoftDeleteFlag;
}

export interface PermissionsPageV2 {
  count: number;
  cursor?: string;
  permissions: PermissionV2[];
}

export enum UpdatePermissionCommandV2 {
  UPDATE_RESOURCE = 'UPDATE_RESOURCE',
  UPDATE_ACTION = 'UPDATE_ACTION',
  UPDATE_DESCRIPTION = 'UPDATE_DESCRIPTION',
  DELETE_PERMISSION = 'DELETE_PERMISSION',
}

export interface UpdatePermissionV2 {
  id: string;
  command: UpdatePermissionCommandV2;
  resource?: ResourceV2;
  action?: ActionV2;
  description?: string;
}

export interface RoleV2 extends Timestamps, Versioned {
  id: string;
  merchant_id: string;
  permissions: PermissionV2[];
  name: string;
  description: string;
}

export interface ProtoRoleV2 extends Omit<RoleV2, 'permissions'> {
  permissions: string[];
}

export interface CreateRoleV2 {
  merchant_id: string;
  name: string;
  description: string;
}

export interface GetRoleV2 {
  id: string;
  include_permissions?: boolean;
}

export interface GetAllRolesV2 {
  merchant_id: string;
  principal_id?: string;
  store_id?: string;
  by_principal?: boolean;
  by_principal_and_store?: boolean;
  by_merchant?: boolean;

  count?: number;
  cursor?: string;

  include_permissions?: boolean;

  include_deleted: SoftDeleteFlag;
}

export interface RolesPageV2 {
  count: number;
  cursor?: string;

  roles: RoleV2[];
}

export enum UpdateRoleCommandV2 {
  ADD_PERMISSIONS = 'ADD_PERMISSIONS',
  REMOVE_PERMISSIONS = 'REMOVE_PERMISSIONS',
  ADD_PRINCIPALS = 'ADD_PRINCIPALS',
  REMOVE_PRINCIPALS = 'REMOVE_PRINCIPALS',
  DELETE_ROLE = 'DELETE_ROLE',
  UPDATE_OTHER = 'UPDATE_OTHER',
}

export interface AddPrincipalToRole {
  principal_id: string;
  store_id: string;
}

export interface UpdateRoleV2 {
  id: string;
  command: UpdateRoleCommandV2;
  permissions?: string[];
  principals?: AddPrincipalToRole[];
  description?: string;
  name?: string;
}

export interface PrincipalV2 extends Timestamps, Versioned {
  id: string;
  merchant_id: string;
}

export interface CreatePrincipalV2 {
  id: string;
  merchant_id: string;
}

export interface GetPrincipalV2 {
  id: string;
}

export enum UpdatePrincipalCommandV2 {
  ADD_ROLES = 'ADD_ROLES',
  REMOVE_ROLES = 'REMOVE_ROLES',
  DELETE_PRINCIPAL = 'DELETE_PRINCIPAL',
}

export interface AddRoleToPrincipal {
  role_id: string;
  store_id: string;
}

export interface UpdatePrincipalV2 {
  id: string;
  command: UpdatePrincipalCommandV2;
  roles: AddRoleToPrincipal[];
}

export interface GetAllPrincipalsByRoleV2 {
  role_id: string;
}

export interface PrincipalsPageV2 {
  count: number;
  cursor?: string;

  principals: string[];
}

export interface PrincipalIdsPageV2 {
  ids: string[];
}

export interface RolePrincipalV2 {
  role_id: string;
  principal_id: string;
  store_id: string;
}

export interface PrincipalRoleV2 extends RolePrincipalV2 {}

export interface PolicyV2 {
  principalId: string;
  policyDocument?: {
    Version: string;
    Statement: Array<{
      Action: string;
      Effect: 'Allow' | 'Deny';
      Resource: string;
    }>;
  };
  context?: {
    [key: string]: string | number | boolean;
  };
}

export enum PolicyDecision {
  Allow = 'Allow',
  Deny = 'Deny',
}

export interface AuthorizeOpenApp {
  app_names: string[];
  principal_id: string;
  store_id?: string;
  merchant_id?: string;
}

export interface VerifyCedarBody {
  policy_string: string;
  principal: string;
  action: string;
  resource: string;
}

export interface VerifyCedarResult {
  allow: boolean;
}

export interface AuthorizeOpenAppResult {
  results: {
    resource: string;
    allow: boolean;
  }[];
}

export interface CreateMerchantRole {
  merchant_id: string;
  store_id: string;
}

/** One decision request: may `principal_id` perform `action` in every store listed. */
export interface AuthorizeActionRequest {
  /** Resource name of an ACTION permission, e.g. `orders:order.create`. */
  action: string;
  /** Every store the action touches; all must allow. */
  store_ids: string[];
}

/** Body of `POST authorizations-v2/authorizer/decisions/{merchant_id}` (service-signed). */
export interface AuthorizeActions {
  principal_id: string;
  actions: AuthorizeActionRequest[];
}

export interface AuthorizeActionDecision extends AuthorizeActionRequest {
  allow: boolean;
}

export interface AuthorizeActionsResult {
  /** Bumped on every role or assignment change of the merchant; cache key. */
  permissions_version: number;
  results: AuthorizeActionDecision[];
}

/** Query of `GET authorizations-v2/authorizer/my-actions/{merchant_id}` (verified user). */
export interface GetMyActions {
  merchant_id: string;
  store_id: string;
}

export interface MyActionsResult {
  permissions_version: number;
  /** Resource names of the ACTION permissions the user holds in the store. */
  actions: string[];
}

/**
 * Body of `POST authorizations-v2/authorizer/team-actions/{merchant_id}`
 * (service-signed): the actions of several principals in one store, for the
 * POS offline credentials. An owner's principal is the merchant id.
 */
export interface GetTeamActions {
  store_id: string;
  principal_ids: string[];
}

/**
 * `GET /authorizer/principal-store-roles/{merchant_id}?principal_id=`
 * (service-only, IAM): the roles a principal holds, per store (role tasks).
 */
export interface GetPrincipalStoreRoles {
  merchant_id: string;
  principal_id: string;
}

export interface PrincipalStoreRoles {
  /** Per store id: the ids of the principal's live roles there. */
  stores: Record<string, string[]>;
}

/**
 * `GET /authorizer/role-principals/{merchant_id}?role_id=&store_id=`
 * (service-only, IAM): who holds a role in one store (role tasks).
 */
export interface GetRolePrincipalsInStore {
  merchant_id: string;
  role_id: string;
  store_id: string;
}

export interface RolePrincipalsInStore {
  role_id: string;
  role_name: string;
  principal_ids: string[];
}

export interface TeamActionsResult {
  permissions_version: number;
  /** Per principal id: resource names of the ACTION permissions it holds. */
  actions: Record<string, string[]>;
}
